import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Pencil, RotateCcw, Video } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

import type { AdminClip } from './clip.types'

// ----------------------------------------------------------------------

interface ClipViewDialogProps {
  clip: AdminClip | null
  categoryLabel?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit?: (clip: AdminClip) => void
  onRetry?: (clip: AdminClip) => void
}

const fmtDuration = (sec: number) => {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

const fmtDate = (iso?: string) => {
  if (!iso) return '-'
  const d = new Date(iso)
  return isNaN(d.getTime())
    ? '-'
    : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
}

function StatusPill({ status }: { status: AdminClip['status'] }) {
  if (status === 'processing')
    return (
      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
        กำลังประมวลผล
      </Badge>
    )
  if (status === 'failed')
    return <Badge className="bg-red-100 text-red-800 border-red-200">ล้มเหลว</Badge>
  return <Badge className="bg-green-100 text-green-800 border-green-200">พร้อม</Badge>
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm text-black break-words">{children}</div>
    </div>
  )
}

export default function ClipViewDialog({
  clip,
  categoryLabel,
  open,
  onOpenChange,
  onEdit,
  onRetry,
}: ClipViewDialogProps) {
  if (!clip) return null

  // Prefer the direct MP4 (plays natively in every browser); HLS only as a last
  // resort (Safari plays it natively, Chrome would need hls.js).
  const playUrl =
    clip.fallbackMp4Url || clip.videoUrl || clip.hlsMasterUrl || ''
  const canRetry = clip.status === 'failed' && clip.storageProvider === 'r2'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <ScrollArea className="max-h-[calc(100dvh-2rem)]">
          <div className="space-y-4 p-5">
            <DialogHeader className="space-y-1">
              <DialogTitle className="pr-6 text-left text-lg leading-snug">
                {clip.title}
              </DialogTitle>
              <DialogDescription className="text-left">
                รายละเอียดคลิป
              </DialogDescription>
            </DialogHeader>

            {/* Inline player (admin preview). Streams the file directly — it does
                NOT count a view; the counter only increments via the public
                POST /clips/:id/view endpoint, which the admin never calls. */}
            {playUrl ? (
              <video
                key={clip.id}
                controls
                playsInline
                preload="metadata"
                poster={clip.thumbnailUrl || undefined}
                src={playUrl}
                className="aspect-video w-full rounded-lg bg-black"
              />
            ) : (
              <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg bg-gray-100">
                {clip.thumbnailUrl ? (
                  <img
                    src={clip.thumbnailUrl}
                    alt={clip.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Video className="h-10 w-10 text-gray-400" />
                )}
              </div>
            )}

            {/* Status + failure reason */}
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={clip.status} />
              {clip.isPublished ? (
                <Badge variant="outline">เผยแพร่แล้ว</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  ยังไม่เผยแพร่
                </Badge>
              )}
            </div>
            {clip.status === 'failed' &&
              (clip.transcodeErrorCode || clip.transcodeErrorMessage) && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm">
                  <p className="font-medium text-red-700">
                    เหตุผลที่ล้มเหลว
                    {clip.transcodeErrorCode ? ` (${clip.transcodeErrorCode})` : ''}
                  </p>
                  {clip.transcodeErrorMessage && (
                    <p className="mt-1 break-words text-red-600/90">
                      {clip.transcodeErrorMessage}
                    </p>
                  )}
                </div>
              )}

            {clip.description && (
              <Field label="คำอธิบาย">
                <p className="whitespace-pre-wrap">{clip.description}</p>
              </Field>
            )}

            <Separator />

            {/* Metadata grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="หมวดหมู่">
                {categoryLabel ?? clip.category ?? 'ทั้งหมด'}
              </Field>
              <Field label="ความยาว">
                <span className="tabular-nums">{fmtDuration(clip.durationSec)}</span>
              </Field>
              <Field label="ยอดชม">
                <span className="tabular-nums">
                  {clip.viewCount?.toLocaleString('th-TH') ?? 0}
                </span>
              </Field>
              <Field label="สัดส่วน">{clip.aspectRatio ?? '-'}</Field>
              <Field label="วันที่อัพโหลด">{fmtDate(clip.createdAt)}</Field>
              <Field label="อัปเดตล่าสุด">{fmtDate(clip.updatedAt)}</Field>
              <Field label="แหล่งจัดเก็บ">{clip.storageProvider ?? 'firebase'}</Field>
              <Field label="Clip ID">
                <span className="font-mono text-xs">{clip.id}</span>
              </Field>
            </div>

            {clip.tags.length > 0 && (
              <Field label="แท็ก">
                <div className="flex flex-wrap gap-1">
                  {clip.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </Field>
            )}

            <DialogFooter className="gap-2 sm:gap-2">
              {canRetry && onRetry && (
                <Button variant="outline" onClick={() => onRetry(clip)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  ลองใหม่
                </Button>
              )}
              {onEdit && (
                <Button onClick={() => onEdit(clip)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  แก้ไข
                </Button>
              )}
            </DialogFooter>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
